import * as THREE from "three";
import { psxMaterial, psxEmissive, type PsxUniforms } from "./psx/PsxMaterial.js";
import { textures } from "./textures/procedural.js";
import { Fixtures } from "./fixtures.js";

/**
 * Reactor control, built from primitives. Units are metres. The player sits at the origin facing -Z.
 * Everything the DOM needs to align with is exported as named anchors (world transforms).
 */
export interface RoomAnchors { crt: THREE.Object3D; intercom: THREE.Object3D; plate: THREE.Object3D; lever: THREE.Object3D; beacon: THREE.Mesh; lamp: THREE.Mesh }
/** Things in the room that move on their own, ticked by the station view. */
export interface RoomMotion { fan: THREE.Object3D[]; needles: { mesh: THREE.Object3D; home: number; span: number; rate: number }[]; flicker: THREE.Mesh[] }

/**
 * The instrument overlay: a pane of glass on a ceiling drop-arm, hung at eye height and off to one
 * side of the console. It is deliberately NOT clamped to the monitor — a plate in front of the
 * picture is just a decal. Hung here you have to turn your head away from the terminal to read it.
 */
export function hangPlate(
  g: THREE.Group, geos: THREE.BufferGeometry[],
  mat: (t: THREE.Texture, uv?: [number, number], tint?: THREE.ColorRepresentation) => THREE.Material,
  tSteel: THREE.Texture, opts: { x: number; y: number; z: number; yaw: number; ceilingY: number; w: number; h: number; glow?: THREE.Material },
): THREE.Object3D {
  const pane = new THREE.Group();
  pane.position.set(opts.x, opts.y, opts.z);
  pane.rotation.y = opts.yaw;
  g.add(pane);
  const box = (w: number, h: number, d: number, x: number, y: number, z: number, tint = 0x8d9893) => {
    const geo = new THREE.BoxGeometry(w, h, d); geos.push(geo);
    const m = new THREE.Mesh(geo, mat(tSteel, [1, 1], tint)); m.position.set(x, y, z); pane.add(m); return m;
  };
  const f = 0.022; // frame thickness
  box(opts.w + f * 2, f, 0.03, 0, opts.h / 2 + f / 2, 0);          // frame: top
  box(opts.w + f * 2, f, 0.03, 0, -opts.h / 2 - f / 2, 0);         // bottom
  box(f, opts.h, 0.03, -(opts.w / 2 + f / 2), 0, 0);               // left
  box(f, opts.h, 0.03, opts.w / 2 + f / 2, 0, 0);                  // right
  // Drop arm to the ceiling, with a clamp at the pane and a mounting plate above.
  const dropH = opts.ceilingY - (opts.y + opts.h / 2);
  const rodGeo = new THREE.CylinderGeometry(0.012, 0.012, dropH, 8); geos.push(rodGeo);
  const rod = new THREE.Mesh(rodGeo, mat(tSteel, [1, 2], 0x7a8580));
  rod.position.set(0, opts.h / 2 + dropH / 2, 0); pane.add(rod);
  box(0.07, 0.05, 0.05, 0, opts.h / 2 + 0.03, 0, 0x66716c);        // clamp
  box(0.16, 0.02, 0.16, 0, opts.h / 2 + dropH - 0.01, 0, 0x66716c); // ceiling mount
  // Backlight: an emissive sheet just behind the print, so the pane is a lit surface rather than a
  // transparency laid over a dark room. This is most of the perceived intensity.
  if (opts.glow) {
    const glowGeo = new THREE.PlaneGeometry(opts.w, opts.h); geos.push(glowGeo);
    const back = new THREE.Mesh(glowGeo, opts.glow); back.position.z = -0.004; pane.add(back);
  }
  const anchor = new THREE.Object3D(); pane.add(anchor);            // the DOM pane mounts here
  return anchor;
}

export type StationKind = "bridge" | "reactor" | "breaker_bay" | "storage";

/** Per-station dialect: one ambient tint, one screen phosphor, one signature object. */
const DIALECT: Record<StationKind, { wall: number; desk: number; screen: number; lamp: number; hazard: boolean }> = {
  bridge:      { wall: 0x7f8fa8, desk: 0x8f98a6, screen: 0xffb347, lamp: 0x7fb0ff, hazard: false },
  reactor:     { wall: 0xffffff, desk: 0x9aa39e, screen: 0x88ff99, lamp: 0xff3322, hazard: true },
  breaker_bay: { wall: 0x9a8f86, desk: 0x7a7670, screen: 0x88ff99, lamp: 0xff3322, hazard: true },
  storage:     { wall: 0x8f9a8c, desk: 0x8a8f86, screen: 0x9cd0ff, lamp: 0xffc070, hazard: false },
};

export function buildReactorRoom(shared: PsxUniforms, kind: StationKind = "reactor"): { group: THREE.Group; anchors: RoomAnchors; fixtures: Fixtures; motion: RoomMotion; dispose(): void } {
  const D = DIALECT[kind];
  const g = new THREE.Group();
  const mats: THREE.Material[] = [];
  const geos: THREE.BufferGeometry[] = [];
  const mat = (t: THREE.Texture, uv: [number, number] = [1, 1], tint?: THREE.ColorRepresentation) => { const m = psxMaterial({ map: t, uvScale: uv, tint }, shared); mats.push(m); return m; };
  const box = (w: number, h: number, d: number, m: THREE.Material, x: number, y: number, z: number, ry = 0) => {
    const geo = new THREE.BoxGeometry(w, h, d); geos.push(geo);
    const mesh = new THREE.Mesh(geo, m); mesh.position.set(x, y, z); mesh.rotation.y = ry; g.add(mesh); return mesh;
  };

  const tGrate = textures.grating(), tPad = textures.padding(), tSteel = textures.steel(), tHaz = textures.hazard(), tCon = textures.console(), tDuct = textures.duct(), tPlate = textures.plate(), tPhos = textures.phosphor();

  // Room shell 4 × 2.6 × 5 m; inward-facing boxes (thin slabs) so backface culling is a non-issue.
  box(4.2, 0.05, 5.2, mat(tGrate, [4, 5]), 0, -1.2, -1);                 // floor
  box(4.2, 0.05, 5.2, mat(tDuct, [4, 5]), 0, 1.4, -1);                    // ceiling
  box(4.2, 2.6, 0.05, mat(tPad, [4, 2.5], D.wall), 0, 0.1, -3.6);         // back wall (in front of the player)
  box(0.05, 2.6, 5.2, mat(tPad, [5, 2.5], D.wall), -2.1, 0.1, -1);        // left wall
  box(0.05, 2.6, 5.2, mat(tPad, [5, 2.5], D.wall), 2.1, 0.1, -1);         // right wall
  box(4.2, 2.6, 0.05, mat(tSteel, [4, 2.5], 0x8c9a94), 0, 0.1, 1.6);      // wall behind (hatch side)
  // Signature objects per station
  if (kind === "reactor") box(3.6, 0.9, 0.08, mat(tCon, [6, 1.5]), 0, 0.55, -3.55);                      // gauge wall
  if (kind === "bridge") { const vpGeo = new THREE.PlaneGeometry(2.6, 1.0); geos.push(vpGeo); const vpMat = psxEmissive(tPhos, shared, 0x223355); mats.push(vpMat); const vp = new THREE.Mesh(vpGeo, vpMat); vp.position.set(0, 0.6, -3.55); g.add(vp); box(2.8, 0.1, 0.12, mat(tSteel, [3, 0.2], 0x6c7570), 0, 1.15, -3.5); box(2.8, 0.1, 0.12, mat(tSteel, [3, 0.2], 0x6c7570), 0, 0.05, -3.5); } // armoured viewport
  if (kind === "breaker_bay") { box(1.6, 1.2, 0.1, mat(tSteel, [2, 1.5], 0x5a6560), 0, 0.4, -3.55); for (const x of [-0.45, -0.15, 0.15, 0.45]) box(0.12, 0.3, 0.12, mat(tSteel, [1, 1], 0x1a1a18), x, 0.4, -3.45); } // breaker board with four bat handles
  if (kind === "storage") { for (const x of [-1.5, -0.9]) for (const y of [-0.7, -0.1, 0.5]) box(0.5, 0.4, 0.5, mat(tSteel, [1, 1], 0x6a6358), x, y, -3.2); box(1.4, 1.8, 0.06, mat(tSteel, [2, 2], 0x55605b), -1.2, 0, -3.5); } // racks of crates
  // Pipe run with flanges, plus a cable bundle sagging beside it — the ceiling should read as built,
  // not as a plane. Cylinders get 10 segments now: at 6 they silhouette as obvious hexagons.
  for (const [x, r] of [[-1.62, 0.085], [-1.34, 0.055]] as const) {
    const geo = new THREE.CylinderGeometry(r, r, 5, 10); geos.push(geo);
    const pipe = new THREE.Mesh(geo, mat(tSteel, [1, 4], 0x76867f));
    pipe.rotation.x = Math.PI / 2; pipe.position.set(x, 1.24, -1); g.add(pipe);
    const fl = new THREE.CylinderGeometry(r * 1.5, r * 1.5, 0.05, 10); geos.push(fl);
    for (const z of [-2.6, -0.6, 1.0]) { const f = new THREE.Mesh(fl, mat(tSteel, [1, 1], 0x5c6a64)); f.rotation.x = Math.PI / 2; f.position.set(x, 1.24, z); g.add(f); }
  }
  for (const [x, r, sag] of [[1.55, 0.05, 0.06], [1.62, 0.04, 0.11], [1.48, 0.035, 0.03]] as const) {
    for (let i = 0; i < 4; i++) {
      const geo = new THREE.CylinderGeometry(r, r, 1.3, 6); geos.push(geo);
      const c = new THREE.Mesh(geo, mat(tSteel, [1, 2], 0x2b2f2d));
      c.rotation.set(Math.PI / 2 + (i % 2 ? sag : -sag), 0, 0);
      c.position.set(x, 1.18 - (i % 2 ? sag * 0.5 : 0), -2.6 + i * 1.3); g.add(c);
    }
  }
  // Ceiling fixture: a caged strip light, the room's only practical.
  box(0.9, 0.06, 0.26, mat(tSteel, [2, 0.5], 0x8d9893), 0.2, 1.33, -1.5);
  const stripGeo = new THREE.PlaneGeometry(0.8, 0.18); geos.push(stripGeo);
  const stripMat = psxEmissive(tPlate, shared, 0xffe6b8); mats.push(stripMat);
  const strip = new THREE.Mesh(stripGeo, stripMat); strip.rotation.x = Math.PI / 2; strip.position.set(0.2, 1.295, -1.5); g.add(strip);
  for (const x of [-0.15, 0.05, 0.25, 0.45]) box(0.02, 0.05, 0.24, mat(tSteel, [1, 1], 0x4d5652), x, 1.295, -1.5);
  // Wall greebles: recessed panels and a conduit box, so the walls have depth at a glance.
  for (const [x, y, z, w, h] of [[-2.05, 0.45, -2.2, 0.5, 0.6], [-2.05, -0.3, -2.2, 0.5, 0.35], [2.05, 0.35, -2.4, 0.6, 0.5]] as const) {
    box(0.06, h, w, mat(tSteel, [1, 1], 0x66716c), x, y, z);
    box(0.03, h - 0.08, w - 0.08, mat(tCon, [1, 1], 0x525c58), x + (x < 0 ? 0.03 : -0.03), y, z);
  }
  box(0.14, 0.3, 0.22, mat(tSteel, [1, 1], 0x7a8580), -2.0, 0.9, -0.6);

  // The console: desk slab, front skirt with hazard stripe, drawers, the CRT housing, keypad, lever block.
  const deskY = -0.32;
  box(2.2, 0.08, 0.8, mat(tSteel, [3, 1], D.desk), 0, deskY, -0.9);            // desk top
  if (D.hazard) box(2.2, 0.06, 0.8, mat(tHaz, [4, 0.3]), 0, deskY - 0.07, -0.9); // hazard edge strip
  box(2.2, 0.78, 0.7, mat(tSteel, [3, 1.2], 0x6c7570), 0, deskY - 0.47, -0.95); // skirt
  for (const x of [-0.75, -0.45]) box(0.26, 0.16, 0.02, mat(tPlate, [1, 0.3], 0x8d887a), x, deskY - 0.3, -0.58); // drawers
  box(1.4, 0.18, 0.5, mat(tCon, [4, 0.5]), 0.05, deskY + 0.13, -0.85);        // sloped instrument bank (flat for now)
  // The CRT: a tapered tube going back, a bezel frame proud of the glass, and the picture inset
  // behind it so it parallaxes when you look around. The DOM terminal mounts on the `crt` anchor.
  const screenW = 0.72, screenH = 0.54, faceZ = -0.92, screenY = deskY + 0.55;
  const tubeGeo = new THREE.CylinderGeometry(0.26, 0.42, 0.6, 4); geos.push(tubeGeo);
  const tube = new THREE.Mesh(tubeGeo, mat(tSteel, [1.5, 1.5], 0x6d7873));
  tube.rotation.set(Math.PI / 2, 0, Math.PI / 4); tube.position.set(0, screenY, faceZ - 0.32); g.add(tube);
  const bezelMat = mat(tSteel, [2, 1.5], 0x9aa5a0);
  const bw = 0.07; // bezel width
  box(screenW + bw * 2, bw, 0.1, bezelMat, 0, screenY + screenH / 2 + bw / 2, faceZ);           // top
  box(screenW + bw * 2, bw, 0.1, bezelMat, 0, screenY - screenH / 2 - bw / 2, faceZ);           // bottom
  box(bw, screenH + bw * 2, 0.1, bezelMat, -(screenW / 2 + bw / 2), screenY, faceZ);            // left
  box(bw, screenH + bw * 2, 0.1, bezelMat, screenW / 2 + bw / 2, screenY, faceZ);               // right
  // Chamfer: a second, smaller frame set back a few mm, so the bezel reads as wrapping over the glass.
  const chamferMat = mat(tSteel, [2, 1.5], 0x8b958f);
  const cw = 0.03;
  box(screenW + cw * 2, cw, 0.05, chamferMat, 0, screenY + screenH / 2 + cw / 2, faceZ - 0.035);
  box(screenW + cw * 2, cw, 0.05, chamferMat, 0, screenY - screenH / 2 - cw / 2, faceZ - 0.035);
  box(cw, screenH + cw * 2, 0.05, chamferMat, -(screenW / 2 + cw / 2), screenY, faceZ - 0.035);
  box(cw, screenH + cw * 2, 0.05, chamferMat, screenW / 2 + cw / 2, screenY, faceZ - 0.035);
  const screenGeo = new THREE.PlaneGeometry(screenW, screenH); geos.push(screenGeo);
  const screenMat = psxEmissive(tPhos, shared, D.screen); mats.push(screenMat);
  const screen = new THREE.Mesh(screenGeo, screenMat); screen.position.set(0, screenY, faceZ - 0.04); g.add(screen);
  const crt = new THREE.Object3D(); crt.position.copy(screen.position); g.add(crt);
  // The screen is the room's lamp (see PsxMaterial): light the console from where the picture is.
  // The virtual lamp sits a hand's width in front of the glass, so the bezel rim and desk catch it.
  shared.uScreenPos.value.copy(screen.position).setZ(screen.position.z + 0.18);
  shared.uScreenColor.value.set(D.screen).multiplyScalar(0.95);
  // Intercom monitor bank above the CRT: three small housings.
  const intercom = new THREE.Object3D(); intercom.position.set(0, deskY + 1.0, -1.05); g.add(intercom);
  for (const x of [-0.18, 0, 0.18] as const) { box(0.175, 0.16, 0.2, mat(tSteel, [1, 1], 0x6c7570), x, deskY + 1.0, -1.18); }
  // Keypad with a red lamp (left), lever block (right)
  box(0.3, 0.22, 0.3, mat(tCon, [1, 1]), -0.85, deskY + 0.15, -0.9);
  const lampGeo = new THREE.BoxGeometry(0.06, 0.06, 0.02); geos.push(lampGeo);
  const lampMat = psxEmissive(tPlate, shared, D.lamp); mats.push(lampMat);
  const lamp = new THREE.Mesh(lampGeo, lampMat); lamp.position.set(-0.85, deskY + 0.27, -0.75); g.add(lamp);
  const leverBase = box(0.3, 0.14, 0.36, mat(tSteel, [1, 1], 0x5a6460), 0.8, deskY + 0.11, -0.85);
  const lever = new THREE.Object3D(); lever.position.set(0.8, deskY + 0.18, -0.85); g.add(lever);
  const stickGeo = new THREE.CylinderGeometry(0.025, 0.03, 0.34, 6); geos.push(stickGeo);
  const stick = new THREE.Mesh(stickGeo, mat(tSteel, [1, 1], 0x3a3f3c)); stick.position.y = 0.17; lever.add(stick);
  const gripGeo = new THREE.CylinderGeometry(0.05, 0.05, 0.1, 8); geos.push(gripGeo);
  const grip = new THREE.Mesh(gripGeo, mat(tSteel, [1, 1], 0x1a1a18)); grip.position.y = 0.36; lever.add(grip);
  lever.rotation.x = -0.35;
  // Engraved plate under the lever
  box(0.28, 0.06, 0.02, mat(tPlate, [1, 0.5]), 0.8, deskY + 0.06, -0.66);
  // Red beacon on the side desk
  const beaconGeo = new THREE.CylinderGeometry(0.06, 0.07, 0.12, 8); geos.push(beaconGeo);
  const beaconMat = psxEmissive(tPlate, shared, 0x330000); mats.push(beaconMat);
  const beacon = new THREE.Mesh(beaconGeo, beaconMat); beacon.position.set(-1.4, deskY + 0.1, -0.8); g.add(beacon);
  // Clutter: a mug (with a handle), a stack of forms, and the chair you are sitting in.
  const mugGeo = new THREE.CylinderGeometry(0.042, 0.036, 0.1, 12); geos.push(mugGeo);
  const mug = new THREE.Mesh(mugGeo, mat(tPlate, [1, 1], 0xd8d2c0)); mug.position.set(-0.35, deskY + 0.09, -0.62); g.add(mug);
  const handleGeo = new THREE.TorusGeometry(0.028, 0.008, 6, 10); geos.push(handleGeo);
  const handle = new THREE.Mesh(handleGeo, mat(tPlate, [1, 1], 0xd8d2c0)); handle.position.set(-0.30, deskY + 0.09, -0.62); handle.rotation.y = Math.PI / 2; g.add(handle);
  box(0.16, 0.012, 0.22, mat(tPlate, [1, 1], 0xc9c3ad), 0.42, deskY + 0.05, -0.6);
  box(0.155, 0.01, 0.215, mat(tPlate, [1, 1], 0xb8b29c), 0.425, deskY + 0.062, -0.605);
  box(0.5, 0.06, 0.42, mat(tSteel, [1, 1], 0x2f3532), 0, deskY - 0.5, 0.55);   // seat
  box(0.46, 0.5, 0.06, mat(tSteel, [1, 1], 0x2f3532), 0, deskY - 0.24, 0.78);  // back
  void leverBase;

  // ---------------------------------------------------------------------------------------------
  // Fixtures you can reach: switches, buttons, a dial, a drawer, a valve. Scenery that moves and
  // remembers where it was left. Tasks bind to these later by id (see fixtures.ts).
  // ---------------------------------------------------------------------------------------------
  const fixtures = new Fixtures();
  const motion: RoomMotion = { fan: [], needles: [], flicker: [] };
  const part = (parent: THREE.Object3D, w: number, h: number, d: number, m: THREE.Material, x: number, y: number, z: number) => {
    const geo = new THREE.BoxGeometry(w, h, d); geos.push(geo);
    const mesh = new THREE.Mesh(geo, m); mesh.position.set(x, y, z); parent.add(mesh); return mesh;
  };
  // Pick volumes are generous and invisible: a 12 mm bat handle is not a mouse target.
  const pickMat = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }); mats.push(pickMat);
  const pick = (parent: THREE.Object3D, w: number, h: number, d: number, x: number, y: number, z: number) => {
    const geo = new THREE.BoxGeometry(w, h, d); geos.push(geo);
    const mesh = new THREE.Mesh(geo, pickMat); mesh.position.set(x, y, z); parent.add(mesh); return mesh;
  };

  // The bezel has a work light under it, the way every instrument console does — otherwise the
  // screen is the only lamp in the room and every control below it falls to flat ambient.
  {
    const strip = new THREE.PlaneGeometry(screenW + 0.1, 0.02); geos.push(strip);
    const stripM = psxEmissive(tPlate, shared, 0xffd9a0); mats.push(stripM);
    const lit = new THREE.Mesh(strip, stripM);
    lit.rotation.x = -Math.PI / 2.4;
    lit.position.set(0, screenY - screenH / 2 - 0.055, faceZ + 0.05); g.add(lit);
    box(screenW + 0.14, 0.03, 0.06, mat(tSteel, [2, 1], 0x6c7570), 0, screenY - screenH / 2 - 0.035, faceZ + 0.06); // its housing
    shared.uDeskPos.value.set(0, screenY - screenH / 2 - 0.06, faceZ + 0.14);
    shared.uDeskColor.value.set(1.0, 0.84, 0.58);
    shared.uDeskPower.value = 1.35;
  }

  const bankY = deskY + 0.22, bankZ = -0.80;
  // Four coolant-pump toggles in a row, each on its own engraved plate.
  const PUMPS = ["COOLANT PUMP 1", "COOLANT PUMP 2", "COOLANT PUMP 3", "COOLANT PUMP 4"];
  PUMPS.forEach((label, i) => {
    const x = -0.36 + i * 0.15;
    part(g, 0.1, 0.012, 0.07, mat(tPlate, [1, 1], 0x8d887a), x, bankY, bankZ);       // label plate
    const base = new THREE.Group(); base.position.set(x, bankY + 0.012, bankZ); g.add(base);
    part(base, 0.05, 0.022, 0.05, mat(tSteel, [1, 1], 0x505955), 0, 0.011, 0);       // switch body
    const handle = new THREE.Group(); handle.position.set(0, 0.022, 0); base.add(handle);
    part(handle, 0.014, 0.052, 0.014, mat(tSteel, [1, 1], 0x1e2220), 0, 0.026, 0);   // bat handle
    part(handle, 0.02, 0.014, 0.02, mat(tSteel, [1, 1], 0xb8bdb6), 0, 0.052, 0);     // tip
    handle.rotation.x = 0.5;
    const pilotGeo = new THREE.BoxGeometry(0.016, 0.008, 0.016); geos.push(pilotGeo);
    const pilotMat = psxEmissive(tPlate, shared, 0x2a0f0c); mats.push(pilotMat);
    const pilot = new THREE.Mesh(pilotGeo, pilotMat); pilot.position.set(x, bankY + 0.014, bankZ + 0.036); g.add(pilot);
    fixtures.add({ id: `reactor.pump.${i + 1}`, label, kind: "switch", pivot: handle,
      hit: pick(base, 0.07, 0.09, 0.07, 0, 0.04, 0), travel: -1.0, lamp: pilot });
  });
  // The guarded one. It is bolted shut: the guard does not lift, and the company would prefer
  // you did not ask about it.
  {
    const x = 0.34;
    part(g, 0.13, 0.012, 0.1, mat(tPlate, [1, 1], 0x8d887a), x, bankY, bankZ);
    const guard = new THREE.Group(); guard.position.set(x, bankY + 0.012, bankZ - 0.04); g.add(guard);
    part(guard, 0.09, 0.006, 0.075, mat(tHaz, [1, 1]), 0, 0.036, 0.04);              // hinged cover
    part(guard, 0.006, 0.04, 0.006, mat(tSteel, [1, 1], 0x9aa5a0), -0.042, 0.018, 0);
    part(guard, 0.006, 0.04, 0.006, mat(tSteel, [1, 1], 0x9aa5a0), 0.042, 0.018, 0);
    const domeGeo = new THREE.CylinderGeometry(0.026, 0.03, 0.02, 10); geos.push(domeGeo);
    const dome = new THREE.Mesh(domeGeo, psxEmissive(tPlate, shared, 0x8a1a10)); mats.push(dome.material as THREE.Material);
    dome.position.set(0, 0.012, 0.04); guard.add(dome);
    fixtures.add({ id: "reactor.purge", label: "EMERGENCY PURGE — BOLTED", kind: "button", pivot: guard,
      hit: pick(guard, 0.13, 0.08, 0.12, 0, 0.03, 0.02), travel: 0, stuck: true });
  }
  // Lamp test: the one control on this desk that does exactly what it says.
  {
    const x = 0.52, z = bankZ + 0.1;
    part(g, 0.1, 0.012, 0.07, mat(tPlate, [1, 1], 0x8d887a), x, bankY, z);
    const cap = new THREE.Group(); cap.position.set(x, bankY + 0.012, z); g.add(cap);
    const capGeo = new THREE.CylinderGeometry(0.022, 0.024, 0.018, 10); geos.push(capGeo);
    const capMesh = new THREE.Mesh(capGeo, mat(tSteel, [1, 1], 0x2b6f4a)); capMesh.position.y = 0.009; cap.add(capMesh);
    fixtures.add({ id: "reactor.lamptest", label: "LAMP TEST", kind: "button", pivot: cap,
      hit: pick(cap, 0.07, 0.06, 0.07, 0, 0.02, 0), travel: -0.012 });
  }
  // Sensor gain: a rotary with six detents and a witness mark that lines up with none of them.
  {
    const x = -0.62, z = -0.72;
    const faceGeo = new THREE.CylinderGeometry(0.05, 0.055, 0.016, 12); geos.push(faceGeo);
    const face = new THREE.Mesh(faceGeo, mat(tCon, [1, 1], 0x59635e)); face.position.set(x, deskY + 0.05, z); g.add(face);
    const knob = new THREE.Group(); knob.position.set(x, deskY + 0.058, z); knob.rotation.x = -Math.PI / 2; g.add(knob);
    const knobGeo = new THREE.CylinderGeometry(0.032, 0.036, 0.03, 10); geos.push(knobGeo);
    const kn = new THREE.Mesh(knobGeo, mat(tSteel, [1, 1], 0x242826)); kn.rotation.x = Math.PI / 2; knob.add(kn);
    part(knob, 0.008, 0.032, 0.006, mat(tSteel, [1, 1], 0xd6d8d0), 0, 0.022, 0.012); // pointer
    fixtures.add({ id: "reactor.gain", label: "SENSOR GAIN", kind: "dial", pivot: knob,
      hit: pick(knob, 0.1, 0.1, 0.06, 0, 0, 0.02), travel: -Math.PI / 3, detents: 6 });
  }
  // The desk drawer. Forms in, forms out.
  {
    const drawer = new THREE.Group(); drawer.position.set(-0.75, deskY - 0.3, -0.58); g.add(drawer);
    part(drawer, 0.26, 0.16, 0.02, mat(tPlate, [1, 0.3], 0x8d887a), 0, 0, 0);
    part(drawer, 0.09, 0.016, 0.03, mat(tSteel, [1, 1], 0x9aa5a0), 0, 0.03, 0.02);   // pull
    part(drawer, 0.24, 0.13, 0.3, mat(tSteel, [1, 1], 0x4a534f), 0, -0.005, -0.16);  // the box behind the face
    part(drawer, 0.2, 0.01, 0.24, mat(tPlate, [1, 1], 0xc9c3ad), 0, 0.03, -0.14);    // a ream of forms
    fixtures.add({ id: "reactor.drawer", label: "FORMS — DHI 22B", kind: "drawer", pivot: drawer,
      hit: pick(drawer, 0.3, 0.2, 0.06, 0, 0, 0.02), travel: 0.26 });
  }
  // Isolation valve on the left wall, at the bottom of the vertical pipe run.
  {
    const vy = -0.35;
    const riserGeo = new THREE.CylinderGeometry(0.07, 0.07, 1.5, 10); geos.push(riserGeo);
    const riser = new THREE.Mesh(riserGeo, mat(tSteel, [1, 3], 0x76867f)); riser.position.set(-1.9, 0.35, -2.4); g.add(riser);
    box(0.2, 0.16, 0.2, mat(tSteel, [1, 1], 0x5c6a64), -1.9, vy, -2.4);              // valve body
    const wheel = new THREE.Group(); wheel.position.set(-1.78, vy, -2.4); wheel.rotation.z = Math.PI / 2; g.add(wheel);
    const rimGeo = new THREE.TorusGeometry(0.12, 0.014, 6, 14); geos.push(rimGeo);
    const rim = new THREE.Mesh(rimGeo, mat(tSteel, [1, 1], 0x8a3a2a)); wheel.add(rim);
    for (const a of [0, Math.PI / 3, (2 * Math.PI) / 3]) {
      const sp = part(wheel, 0.24, 0.012, 0.012, mat(tSteel, [1, 1], 0x8a3a2a), 0, 0, 0); sp.rotation.z = a;
    }
    const stemGeo = new THREE.CylinderGeometry(0.018, 0.018, 0.12, 8); geos.push(stemGeo);
    const stem = new THREE.Mesh(stemGeo, mat(tSteel, [1, 1], 0x9aa5a0)); stem.rotation.z = Math.PI / 2; stem.position.set(-1.84, vy, -2.4); g.add(stem);
    fixtures.add({ id: "reactor.valve.iso", label: "COOLANT ISOLATION", kind: "wheel", pivot: wheel,
      hit: pick(wheel, 0.3, 0.3, 0.1, 0, 0, 0), travel: -Math.PI * 1.5 });
  }

  // ---------------------------------------------------------------------------------------------
  // Detail pass: the room should look worked in. Nothing here is interactive.
  // ---------------------------------------------------------------------------------------------
  // A second strip light, further back and failing — the room is lit unevenly on purpose.
  {
    box(0.7, 0.06, 0.22, mat(tSteel, [2, 0.5], 0x8d9893), -1.25, 1.33, -2.6);
    const geo = new THREE.PlaneGeometry(0.62, 0.15); geos.push(geo);
    const m = psxEmissive(tPlate, shared, 0xffe6b8); mats.push(m);
    const tube2 = new THREE.Mesh(geo, m); tube2.rotation.x = Math.PI / 2; tube2.position.set(-1.25, 1.295, -2.6); g.add(tube2);
    motion.flicker.push(tube2);
  }
  // Gauge cluster: three dials with needles that never quite settle.
  if (kind === "reactor") {
    const dialGeo = new THREE.CylinderGeometry(0.09, 0.09, 0.03, 12); geos.push(dialGeo);
    const faceGeo = new THREE.CircleGeometry(0.078, 12); geos.push(faceGeo);
    [-0.7, -0.35, 0].forEach((x, i) => {
      const body = new THREE.Mesh(dialGeo, mat(tSteel, [1, 1], 0x6c7570));
      body.rotation.x = Math.PI / 2; body.position.set(x, 0.72, -3.5); g.add(body);
      const face = new THREE.Mesh(faceGeo, mat(tPlate, [1, 1], 0xd9d4c2)); face.position.set(x, 0.72, -3.47); g.add(face);
      const needle = new THREE.Group(); needle.position.set(x, 0.72, -3.46); g.add(needle);
      part(needle, 0.005, 0.13, 0.004, mat(tPlate, [1, 1], 0x8a1a10), 0, 0.05, 0);
      motion.needles.push({ mesh: needle, home: -0.7 + i * 0.35, span: 0.16 + i * 0.05, rate: 0.5 + i * 0.31 });
    });
    // Punch-card slot and a paper tape spool beside the gauges.
    box(0.28, 0.03, 0.06, mat(tSteel, [1, 1], 0x3c4441), 0.55, 0.72, -3.5);
    const spoolGeo = new THREE.CylinderGeometry(0.09, 0.09, 0.03, 12); geos.push(spoolGeo);
    const spool = new THREE.Mesh(spoolGeo, mat(tPlate, [1, 1], 0xc9c3ad));
    spool.rotation.x = Math.PI / 2; spool.position.set(0.9, 0.72, -3.48); g.add(spool);
  }
  // Extract fan in the right wall: a caged duct with blades that turn.
  {
    box(0.5, 0.5, 0.06, mat(tSteel, [1, 1], 0x5f6a65), 2.04, 0.55, -1.4);
    const fan = new THREE.Group(); fan.position.set(2.0, 0.55, -1.4); fan.rotation.z = Math.PI / 2; g.add(fan);
    for (const a of [0, Math.PI / 2.5, (2 * Math.PI) / 2.5, (3 * Math.PI) / 2.5, (4 * Math.PI) / 2.5]) {
      const b = part(fan, 0.03, 0.34, 0.02, mat(tSteel, [1, 1], 0x39413d), 0, 0, 0);
      b.rotation.z = a; b.rotation.y = 0.4;
    }
    motion.fan.push(fan);
    for (const y of [-0.16, -0.05, 0.06, 0.17]) box(0.02, 0.012, 0.44, mat(tSteel, [1, 1], 0x2b302e), 1.99, 0.55 + y, -1.4);
  }
  // The hatch you came in through, on the wall behind the seat.
  {
    box(0.9, 1.7, 0.08, mat(tSteel, [1.5, 2.5], 0x77827d), 1.1, -0.25, 1.55);
    box(1.0, 1.8, 0.05, mat(tSteel, [1.5, 2.5], 0x5d6763), 1.1, -0.25, 1.58);
    const hw = new THREE.Mesh(new THREE.TorusGeometry(0.11, 0.016, 6, 12), mat(tSteel, [1, 1], 0x8a3a2a));
    geos.push(hw.geometry); hw.position.set(1.45, -0.25, 1.5); g.add(hw);
    for (const y of [0.45, -0.95]) box(0.1, 0.06, 0.1, mat(tSteel, [1, 1], 0x4a534f), 0.68, y - 0.25, 1.5);
    box(0.34, 0.12, 0.02, mat(tHaz, [2, 0.3]), 1.1, 0.68, 1.5);                      // stencil over the hatch
  }
  // Floor kit: a toolbox, a coiled cable spool, and a bucket that has been there a while.
  box(0.34, 0.16, 0.2, mat(tSteel, [1, 1], 0x7a5a2a), -1.35, -1.09, -0.2);
  box(0.1, 0.03, 0.03, mat(tSteel, [1, 1], 0x2b302e), -1.35, -1.0, -0.2);
  {
    const coilGeo = new THREE.TorusGeometry(0.17, 0.045, 6, 14); geos.push(coilGeo);
    const coil = new THREE.Mesh(coilGeo, mat(tSteel, [2, 1], 0x2b2f2d));
    coil.rotation.x = Math.PI / 2; coil.position.set(1.45, -1.12, -0.5); g.add(coil);
    const bucketGeo = new THREE.CylinderGeometry(0.11, 0.09, 0.24, 10); geos.push(bucketGeo);
    const bucket = new THREE.Mesh(bucketGeo, mat(tSteel, [1, 1], 0x6a736e)); bucket.position.set(-1.75, -1.05, -1.6); g.add(bucket);
  }
  // Paperwork culture: a clipboard on the wall, two placards, and a taped-up notice.
  box(0.02, 0.3, 0.22, mat(tPlate, [1, 1], 0xc9c3ad), -2.03, 0.15, -1.2);
  box(0.025, 0.04, 0.2, mat(tSteel, [1, 1], 0x9aa5a0), -2.03, 0.31, -1.2);
  box(0.02, 0.12, 0.3, mat(tPlate, [1, 1], 0xd9d4c2), 2.03, 0.95, -2.2);
  box(0.02, 0.16, 0.24, mat(tPlate, [1, 1], 0xd9d4c2), -2.03, 0.85, -2.9);
  // Conduit dropping down the back corner into a junction box.
  {
    const geo = new THREE.CylinderGeometry(0.03, 0.03, 1.9, 8); geos.push(geo);
    const c = new THREE.Mesh(geo, mat(tSteel, [1, 3], 0x6a736e)); c.position.set(1.95, 0.25, -3.4); g.add(c);
    box(0.16, 0.22, 0.12, mat(tSteel, [1, 1], 0x7a8580), 1.95, -0.8, -3.4);
  }

  // Hung right in front of the seat, an arm's length from the face — a different focal plane from the
  // console entirely, so the eye has to choose between the pane and the picture behind it.
  const plateGlow = psxEmissive(tPhos, shared, 0x183d4a); mats.push(plateGlow);
  const plate = hangPlate(g, geos, mat, tSteel, { x: -0.015, y: 0.5, z: 0.5, yaw: 0.1, ceilingY: 1.37, w: 0.32, h: 0.24, glow: plateGlow });

  return {
    group: g,
    anchors: { crt, intercom, plate, lever, beacon, lamp },
    fixtures, motion,
    dispose() { for (const m of mats) m.dispose(); for (const geo of geos) geo.dispose(); for (const t of [tGrate, tPad, tSteel, tHaz, tCon, tDuct, tPlate, tPhos]) t.dispose(); },
  };
}
